import { useTranslation } from "react-i18next";
import { Icon } from '@iconify/react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface CreateCompanyCancelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  hasCompany: boolean;
  isDeleting: boolean;
}

export function CreateCompanyCancelDialog({
  open,
  onOpenChange,
  onConfirm,
  hasCompany,
  isDeleting,
}: CreateCompanyCancelDialogProps) {
  const { t } = useTranslation();

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Icon icon="mdi:alert-circle" className="h-5 w-5 text-warning" />
            {t('company.create.cancelTitle') || 'Cancel Company Creation'}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <span>
              {hasCompany
                ? t('company.create.cancelConfirmWithDelete') ||
                  'Are you sure you want to cancel? This will delete the company you created. This action cannot be undone.'
                : t('company.create.cancelConfirm') ||
                  'Are you sure you want to cancel? Your progress will be saved and you can continue later.'}
            </span>
            {hasCompany && (
              <span className="block text-sm text-gray-600 mt-2">
                {t('company.create.cancelWarning') ||
                  'This will permanently delete the company and all associated data.'}
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>
            {t('common.cancel') || 'Keep Creating'}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isDeleting}
            className={hasCompany ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90 text-white' : ''}
          >
            {isDeleting
              ? t('common.loading') || 'Deleting...'
              : hasCompany
              ? t('company.create.confirmDelete') || 'Yes, Delete Company'
              : t('company.create.confirmCancel') || 'Yes, Cancel'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

